import { useCallback, useRef, useState } from "react";
import imageCompression from "browser-image-compression";
import { useReactToPrint } from "react-to-print";
// src/services/image-optimization.service.ts
const DEFAULT_OPTIONS = {
	maxSizeMB: 1,
	quality: 1,
	useWebWorker: true
};
const optimizeImage = async (file, options = {}) => {
	const compressionOptions = {
		...DEFAULT_OPTIONS,
		...options
	};
	try {
		const compressedBlob = await imageCompression(file, compressionOptions);
		return new File([compressedBlob], file.name, {
			type: file.type,
			lastModified: Date.now()
		});
	} catch (error) {
		throw new Error(`Error optimizing image: ${error}`);
	}
};
// src/hooks/useImageUpload.ts
const useImageUpload = (uploadFn, options = {}) => {
	const [isUploading, setIsUploading] = useState(false);
	const [error, setError] = useState(null);
	const { optimizationOptions = {}, onUploadStart, onUploadSuccess, onUploadError } = options;
	return {
		handleUpload: useCallback(async (file) => {
			try {
				setIsUploading(true);
				setError(null);
				onUploadStart?.();
				const result = await uploadFn(await optimizeImage(file, optimizationOptions));
				onUploadSuccess?.(result);
				return result;
			} catch (error) {
				const newError = error instanceof Error ? error : /* @__PURE__ */ new Error("Unknown error occurred while uploading image");
				setError(newError);
				onUploadError?.(newError);
				throw newError;
			} finally {
				setIsUploading(false);
			}
		}, [
			uploadFn,
			optimizationOptions,
			onUploadStart,
			onUploadSuccess,
			onUploadError
		]),
		isUploading,
		error,
		clearError: useCallback(() => {
			setError(null);
		}, [])
	};
};
// src/hooks/usePagination.ts
function usePagination(items, pageSize) {
	const [page, setPage] = useState(1);
	const totalPages = Math.ceil(items.length / pageSize);
	const currentPage = Math.min(page, Math.max(totalPages, 1));
	if (currentPage !== page) setPage(currentPage);
	const visibleItems = items.slice((currentPage - 1) * pageSize, currentPage * pageSize);
	const onPageChange = ({ page }) => {
		setPage(page);
	};
	return {
		page: currentPage,
		onPageChange,
		visibleItems,
		count: items.length,
		pageSize
	};
}
// src/hooks/usePrint.ts
const usePrint = (options = {}) => {
	const contentRef = useRef(null);
	return {
		contentRef,
		print: useReactToPrint({
			contentRef,
			...options
		})
	};
};
export { useImageUpload, usePagination, usePrint };

//# sourceMappingURL=index.js.map